import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import AuthLayout from './AuthLayout';
import LoginCarousel from './LoginCarousel';
import BeuLoader from '../common/BeuLoader';
import { paymentActions } from '../../_actions';
import { userService } from '../../_services';
import './auth.css';

class SignupRoleSelector extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isProcessingPayment: false,
      error: '',
      successMessage: '',
      initialLoading: true
    };
  }

  componentDidMount() {
    // Check if this is a payment success callback
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      this.handlePaymentSuccess(sessionId);
    }
    
    // Show beu loader for 1.5 seconds on initial load
    setTimeout(() => {
      this.setState({ initialLoading: false });
    }, 1500);
  }

  async handlePaymentSuccess(sessionId) {
    this.setState({ isProcessingPayment: true, error: '' });

    try {
      // The account is created and logged in BEFORE payment (ProfessionalSignup
      // step-4 register), so all that's left is linking the Stripe
      // subscription. (The old path that created the account here, from
      // localStorage 'pendingSignupData', ran on the legacy v1 backend and
      // nothing writes that key any more.)
      localStorage.removeItem('pendingSignupData');
      const accessToken = localStorage.getItem('accessToken');
      const existingSalonId = localStorage.getItem('salonId');
      if (accessToken) {
        // Legacy /profile only returns the hairdresser block (isSalonOwner →
        // owner-gated UI) when defaultRedirectTo===1; the boot profile fetch
        // after this reload needs it set, or the owner loads role-less.
        localStorage.setItem('defaultRedirectTo', 1);
        try {
          await this.props.dispatch(paymentActions.subscriptionRetrieve({ sessionId }));
        } catch (e) {
          console.error('Subscription retrieve (new flow, non-fatal):', e);
        }
        try {
          // Link the subscription's Stripe customer to the owner so marketing
          // SMS overages etc. can bill the card they just paid with.
          await userService.linkSubscriptionBilling({ sessionId });
        } catch (e) {
          console.error('Link subscription billing (new flow, non-fatal):', e);
        }
        if (existingSalonId) {
          // ?setup=1 opens the AI onboarding / import layer over the diary
          // (ONBOARDING-IMPORT-PLAN.md §4.1). Dismissible; never a gate.
          window.location.href = `/diary/${existingSalonId}?setup=1`;
        } else {
          this.props.history.push('/dashboard/account');
        }
        return;
      }
      this.setState({
        isProcessingPayment: false,
        error: 'Signup data not found. Please try signing up again.'
      });
    } catch (error) {
      console.error('Payment success handling error:', error);
      const errorMessage = error.response?.data?.error?.responseMessage
        || error.response?.data?.message
        || error.message
        || 'An error occurred during signup.';
      this.setState({
        isProcessingPayment: false,
        error: errorMessage
      });
    }
  }

  render() {
    const { isProcessingPayment, error, successMessage, initialLoading } = this.state;

    // Show beu loader during initial loading
    if (initialLoading) {
      return <BeuLoader message="Let's get started!" subtitle="Preparing your options..." />;
    }

    // Show loading state while processing payment
    if (isProcessingPayment) {
      return (
        <AuthLayout rightPanel={<LoginCarousel />}>
          <div className="auth-form-container auth-centered">
            <h1 className="auth-title">Setting Up Your Account</h1>
            <p className="auth-subtitle">Please wait while we complete your registration...</p>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div className="auth-button auth-button-loading" style={{ width: '60px', height: '60px', margin: '0 auto', borderRadius: '50%' }}></div>
              <p className="auth-helper-text" style={{ marginTop: '20px' }}>
                This may take a few moments...
              </p>
            </div>
          </div>
        </AuthLayout>
      );
    }

    // Show error state
    if (error) {
      return (
        <AuthLayout rightPanel={<LoginCarousel />}>
          <div className="auth-form-container">
            <Link to="/login" className="auth-nav-back">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>

            <h1 className="auth-title">Something Went Wrong</h1>
            <p className="auth-subtitle">{error}</p>

            <button
              className="auth-button auth-button-primary"
              onClick={() => this.props.history.push('/signup/professional')}
            >
              Try Again
            </button>

            <div className="auth-footer-link">
              <Link to="/login" className="auth-link">
                Already have an account? Sign In
              </Link>
            </div>
          </div>
        </AuthLayout>
      );
    }

    // Show success state
    if (successMessage) {
      return (
        <AuthLayout rightPanel={<LoginCarousel />}>
          <div className="auth-form-container auth-centered">
            <h1 className="auth-title">Welcome to Beu!</h1>
            <p className="auth-subtitle">{successMessage}</p>
            <p className="auth-helper-text">Redirecting to login...</p>
          </div>
        </AuthLayout>
      );
    }

    // Normal role selector view
    return (
      <AuthLayout rightPanel={<LoginCarousel />}>
        <div className="auth-form-container">
          <Link to="/" className="auth-nav-back">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>

          <h1 className="auth-title">Join Beu Today</h1>
          <p className="auth-subtitle">Choose how you want to use Beu</p>

          <div className="auth-role-selector-clean">
            <div
              className="auth-role-card-clean"
              onClick={() => {
                // Carry any returnUrl through so a booking-flow signup that
                // detoured via this selector still lands back in the flow.
                const returnUrl = new URLSearchParams(window.location.search).get('returnUrl');
                this.props.history.push(returnUrl
                  ? `/signup/client?returnUrl=${encodeURIComponent(returnUrl)}`
                  : '/signup/client');
              }}
            >
              <div className="auth-role-card-content">
                <h3 className="auth-role-title-clean">Beu for customers</h3>
                <p className="auth-role-description-clean">Book hair, barbers, beauty & spas near you</p>
              </div>
              <div className="auth-role-arrow">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>

            <div
              className="auth-role-card-clean"
              onClick={() => this.props.history.push('/signup/professional')}
            >
              <div className="auth-role-card-content">
                <h3 className="auth-role-title-clean">Beu for professionals</h3>
                <p className="auth-role-description-clean">Manage and grow your business</p>
              </div>
              <div className="auth-role-arrow">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          </div>

          <div className="auth-footer-link">
            <span className="auth-text-muted">Already have an account? </span>
            <Link to="/login" className="auth-link">Sign in</Link>
          </div>
        </div>
      </AuthLayout>
    );
  }
}

function mapStateToProps(state) {
  return {};
}

export default connect(mapStateToProps)(SignupRoleSelector);